/**
 * @fileoverview Syndicator Showdown — useNotificationFeed
 * @module hooks/useNotificationFeed
 *
 * Single shared Firestore listener for a user's `notifications/{uid}/items`
 * feed. Deliberately fetches the recent feed unfiltered (rather than a
 * `where('status','==','unread')` query) so both the toast queue and the
 * notification center can derive what they need client-side, and so legacy
 * docs that predate the `status` field (welcome/broadcast notifications)
 * still work correctly via the `read` boolean fallback in deriveStatus().
 *
 * Used independently by NotificationToastHost, NotificationBell and
 * NotificationCenter — each mounts its own listener (cheap, capped query),
 * keeping the components decoupled and reusable on their own.
 */

import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query, limit as fbLimit } from 'firebase/firestore';
import { db } from '../services/firebase';
import { deriveStatus, isExpired } from '../utils/notificationHelpers';

export function useNotificationFeed(user, { limit = 30 } = {}) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setItems([]); setLoading(false); return undefined; }

    const q = query(
      collection(db, 'notifications', user.uid, 'items'),
      orderBy('createdAt', 'desc'),
      fbLimit(limit)
    );

    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => {
      setItems([]);
      setLoading(false);
    });

    return unsub;
  }, [user, limit]);

  const unreadItems = items.filter((n) => deriveStatus(n) === 'unread' && !isExpired(n));

  return { items, unreadItems, unreadCount: unreadItems.length, loading };
}

export default useNotificationFeed;
